import { useCallback, useEffect } from 'react'
import { Edge, Node, ReactFlowInstance } from 'reactflow'
import { toast } from 'react-hot-toast/headless'
import { createUuid } from '../../../utils'
import { createEdge, getSourcePath } from './JourneyEditorHelpers'

const CLIPBOARD_TYPE = 'journey-nodes'

interface UseJourneyCopyPasteProps {
    nodes: Node[]
    edges: Edge[]
    isDraft: boolean
    flowInstance: ReactFlowInstance | null
    handleSetNodes: (nodes: Node[]) => void
    setEdges: (edges: Edge[]) => void
}

export function useJourneyCopyPaste({
    nodes,
    edges,
    isDraft,
    flowInstance,
    handleSetNodes,
    setEdges,
}: UseJourneyCopyPasteProps) {
    const onCopy = useCallback(async () => {
        if (!isDraft) return
        const selectedNodes = nodes.filter(n => n.selected)
        if (selectedNodes.length === 0) return

        const selectedNodeIds = new Set(selectedNodes.map(n => n.id))
        const selectedEdges = edges.filter(
            e => selectedNodeIds.has(e.source) && selectedNodeIds.has(e.target),
        )

        const clipboardData = {
            type: CLIPBOARD_TYPE,
            nodes: selectedNodes,
            edges: selectedEdges,
        }

        try {
            await navigator.clipboard.writeText(JSON.stringify(clipboardData))
            toast.success(`Copied ${selectedNodes.length} step${selectedNodes.length > 1 ? 's' : ''}`)
        } catch (error) {
            console.error('Failed to copy to clipboard:', error)
            toast.error('Failed to copy to clipboard')
        }
    }, [nodes, edges, isDraft])

    const onPaste = useCallback(async () => {
        if (!isDraft || !flowInstance) return

        try {
            const clipboardText = await navigator.clipboard.readText()
            const clipboardData = JSON.parse(clipboardText)

            if (clipboardData.type !== CLIPBOARD_TYPE) return

            // Create a mapping from old IDs to new UUIDs
            const idMapping: { [oldId: string]: string } = {}
            clipboardData.nodes.forEach((node: Node) => {
                idMapping[node.id] = createUuid()
            })

            const centerPosition = flowInstance.project({
                x: window.innerWidth / 2,
                y: window.innerHeight / 2,
            })

            // Create new nodes with new UUIDs and updated positions
            const pastedNodes = clipboardData.nodes.map((node: Node) => ({
                ...node,
                id: idMapping[node.id],
                data: {
                    ...node.data ?? {},
                    name: node.data.name ? node.data.name + ' (Copy)' : undefined,
                },
                position: {
                    x: centerPosition.x + (node.position.x - clipboardData.nodes[0].position.x),
                    y: centerPosition.y + (node.position.y - clipboardData.nodes[0].position.y),
                },
                selected: true,
            }))

            // Create new edges with updated source and target IDs
            const pastedEdges = clipboardData.edges
                .filter((edge: Edge) => idMapping[edge.source] && idMapping[edge.target])
                .map((edge: Edge) => createEdge({
                    sourceId: idMapping[edge.source],
                    targetId: idMapping[edge.target],
                    data: edge.data ?? {},
                    path: getSourcePath(edge.sourceHandle!),
                }))

            handleSetNodes([...nodes.map(n => ({ ...n, selected: false })), ...pastedNodes])
            setEdges([...edges.map(e => ({ ...e, selected: false })), ...pastedEdges])

            toast.success(`Pasted ${pastedNodes.length} step${pastedNodes.length > 1 ? 's' : ''}`)
        } catch (error) {
            console.error('Failed to paste from clipboard:', error)
        }
    }, [nodes, edges, isDraft, flowInstance, handleSetNodes, setEdges])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const isMac = navigator.userAgent.toUpperCase().includes('MAC')
            const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey

            if (!isCtrlOrCmd) return

            const target = event.target as HTMLElement
            const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

            if (isInputField) return

            if (event.key === 'c' || event.key === 'C') {
                event.preventDefault()
                void onCopy()
            } else if (event.key === 'v' || event.key === 'V') {
                event.preventDefault()
                void onPaste()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [onCopy, onPaste])
}
